import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "@phosphor-icons/react";

const passwordRequirements = (pw) => ({
  length: pw.length >= 6,
  digit: /\d/.test(pw),
  special: /[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/.test(pw),
});

const Req = ({ ok, children }) => (
  <li className={`flex items-center gap-2 ${ok ? "text-emerald-600" : "text-stone-400"}`}>
    <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-stone-300"}`}></span>
    {children}
  </li>
);

export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.must_change_password;

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const req = passwordRequirements(newPw);
  const allOk = req.length && req.digit && req.special;  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) { toast.error("Le mot de passe ne respecte pas les critères de sécurité."); return; }
    if (newPw !== confirmPw) { toast.error("Les deux mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: forced ? undefined : currentPw,
        new_password: newPw,
      });
      await refresh();
      toast.success("Mot de passe mis à jour ✅");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-12" data-testid="change-password-page">
        <div className="bg-white rounded-3xl p-8 border border-rose-100 shadow-[0_8px_30px_rgba(244,63,94,0.08)]">
          <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-rose-50 text-rose-600 mb-3">
            <ShieldCheck size={28} weight="duotone" />
          </div>
          <h1 className="font-display text-2xl font-black text-stone-800 text-center">
            {forced ? "Sécurisez votre compte" : "Changer mon mot de passe"}
          </h1>
          {forced && (
            <p className="text-sm text-stone-600 text-center mt-2 leading-relaxed">
              Pour renforcer la sécurité de Doudou Alert, merci de définir un nouveau mot de passe plus robuste.
            </p>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {!forced && (
              <div>
                <Label htmlFor="current">Mot de passe actuel</Label>
                <Input id="current" type="password" required value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="rounded-2xl mt-1 py-6" data-testid="current-password-input" />
              </div>
            )}
            <div>
              <Label htmlFor="new">Nouveau mot de passe</Label>
              <Input id="new" type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)} className="rounded-2xl mt-1 py-6" data-testid="new-password-input" />
            </div>
            <div>
              <Label htmlFor="confirm">Confirmer</Label>
              <Input id="confirm" type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="rounded-2xl mt-1 py-6" data-testid="confirm-password-input" />
            </div>

            <ul className="text-sm bg-rose-50/60 rounded-2xl p-4 space-y-1.5">
              <li className="text-stone-700 font-semibold mb-1 flex items-center gap-1.5"><Lock size={14} weight="duotone" /> Critères de sécurité :</li>
              <Req ok={req.length}>6 caractères minimum</Req>
              <Req ok={req.digit}>Au moins 1 chiffre</Req>
              <Req ok={req.special}>Au moins 1 caractère spécial (!@#$%&*…)</Req>
            </ul>

            <Button type="submit" disabled={loading || !allOk} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold shadow-lg shadow-orange-500/30" data-testid="change-password-submit">
              {loading ? "Enregistrement..." : "Mettre à jour le mot de passe"}
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
